import useCommandFlowStore from "../../store/commandFlowStore";

/**
 * Gets commonly used store actions in a single object
 * @returns {Object} Object containing frequently used store actions
 */
export function getStoreActions() {
  const store = useCommandFlowStore.getState();
  return {
    setIsLoading: store.setIsLoading,
    setInSession: store.setInSession,
    setCurrentPrompt: store.setCurrentPrompt,
    setCurrentPromptWithTypingEffect: store.setCurrentPromptWithTypingEffect,
    addOutputWithTypingEffect: store.addOutputWithTypingEffect,
    setOutput: store.setOutput,
    setOutputWithDelay: store.setOutputWithDelay,
    addOutputLinesWithDelay: store.addOutputLinesWithDelay,
  };
}

/**
 * Gets current store state values
 * @returns {Object} Object containing current store state
 */
export function getStoreState() {
  const store = useCommandFlowStore.getState();
  return {
    output: store.output,
    isLoading: store.isLoading,
    inSession: store.inSession,
    currentPrompt: store.currentPrompt,
  };
}

/**
 * Updates session state (loading and session status)
 * @param {boolean} isLoading - Loading state
 * @param {boolean} inSession - Session state
 */
export function updateSessionState(isLoading, inSession) {
  const { setIsLoading, setInSession } = getStoreActions();
  if (typeof isLoading !== "undefined") setIsLoading(isLoading);
  if (typeof inSession !== "undefined") setInSession(inSession);
}

/**
 * Clears session state (sets prompt to null and session to false)
 * @param {Function} onCommandComplete - Optional callback for command completion
 * @param {string} commandName - Optional command name for completion callback
 * @param {Object} sessionData - Optional session data for completion callback
 */
export function clearSessionState(onCommandComplete, commandName, sessionData) {
  // Use a single atomic set() to prevent intermediate renders
  // that could briefly flash the loading indicator
  const store = useCommandFlowStore.getState();
  useCommandFlowStore.setState({
    currentPrompt: null,
    inSession: false,
    isLoading: false,
  });

  if (onCommandComplete) {
    onCommandComplete(commandName, sessionData);
  }
}

/**
 * Sets loading state with error handling
 * @param {boolean} loading - The loading state to set
 */
export function setLoadingState(loading) {
  const { setIsLoading } = getStoreActions();
  setIsLoading(loading);
}

/**
 * Displays output with typing effect, handling multi-line content
 * @param {string|Array} output - The output to display
 * @returns {Promise<void>}
 */
export async function displayOutputWithTyping(output) {
  const { addOutputWithTypingEffect } = getStoreActions();

  if (Array.isArray(output)) {
    await addOutputWithTypingEffect(output);
  } else {
    const outputLines = [{ type: "output", text: output }];
    await addOutputWithTypingEffect(outputLines);
  }
}
